import React from "react";
import moment from "moment";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import T from "prop-types";
import _, { get, xor } from "lodash";
import { Col } from "react-bootstrap";
import {
  reduxForm,
  Field,
  FieldArray,
  formValueSelector,
  getFormInitialValues
} from "redux-form";
import {
  FormInput,
  FormRow,
  Select,
  MonthYearField
} from "MODULES/shared/components/Forms";
import {
  DashedSeparator,
  InventoryStatusCell,
  Warning
} from "MODULES/shared/components";
import { bulkUpdate } from "MODULES/inventories/actions";
import { toOptions } from "LIB/forms";
import {
  EXCHANGE_SRP,
  INVENTORIES,
  REPAIR_AND_HOLD,
  SRP_CHANGE_REASONS,
  SRP_CONDITION,
  SRP_REASON_CHANGED,
  SUGGESTED_RETAIL,
  TAG_DATE
} from "MODULES/inventories/constants";
import AppForm from "MODULES/shared/components/Forms/AppForm";
import { ADMIN, QUALITY_CONTROL, ROLE, USER } from "MODULES/users/constants";
import MinimumSrpWarning from "MODULES/inventories/components/Detail/MinimumSrpWarning";
import { MINIMUM_EXCHANGE_SRP, SETTINGS } from "APP_ROOT/constants";
import { displayFields } from "./constants";
import validate from "./validate";

const AdjustedLast30DaysWarning = ({
  selected,
  inventory: {
    suggested_retail_audit: suggestedRetailAudit,
    serial_number: serialNumber
  }
}) => {
  if (
    selected &&
    suggestedRetailAudit &&
    moment(suggestedRetailAudit.updated_at).isAfter(
      moment().subtract(30, "day")
    )
  ) {
    return (
      <Warning>
        SRP for SN {serialNumber} has been updated within 30 days.
      </Warning>
    );
  }

  return null;
};

export const shouldShowWarning = ({ inventory, suggestedRetail }) => {
  const lineItem = get(inventory, "repair_order_line_items[0]");
  if (!lineItem) return null;
  const repairCost =
    parseFloat(lineItem.labor_cost) +
    parseFloat(lineItem.outside_labor_cost) +
    parseFloat(lineItem.part_cost);

  return (
    inventory.repair_status === REPAIR_AND_HOLD &&
    Number(repairCost) > 0 &&
    repairCost > Number(suggestedRetail)
  );
};

const RepairWarning = ({ selected, suggestedRetail, inventory }) => {
  if (!selected) return null;

  if (shouldShowWarning({ inventory, suggestedRetail })) {
    return (
      <Warning warningType="danger">
        SRP must be greater than the current estimated repair cost.
      </Warning>
    );
  }

  return null;
};

RepairWarning.propTypes = {
  selected: T.bool.isRequired,
  suggestedRetail: T.number,
  inventory: T.shape({})
};

AdjustedLast30DaysWarning.propTypes = {
  selected: T.bool.isRequired,
  inventory: T.shape({ suggested_retail_audit: T.shape({}) })
};

class Form extends React.Component {
  componentWillMount() {
    this.props.setInventoriesSelected(this.props.inventories.map(i => i.id));
  }

  toggleInventory = (id, item) => {
    const inventoriesSelected = xor(this.props.inventoriesSelected, [id]);
    this.props.setInventoriesSelected(inventoriesSelected);
    if (!inventoriesSelected.includes(id)) {
      [SUGGESTED_RETAIL, EXCHANGE_SRP, SRP_CONDITION].forEach(f => {
        const field = `${item}.${f}`;
        this.props.change(field, this.props.getInitialFieldValue(field));
      });
    }
  };

  toggleCondition = condition => {
    const selectedIds = this.props.inventories
      .filter(i => i.srp_condition === condition)
      .map(i => i.id);

    this.props.setInventoriesSelected(selectedIds);
  };

  handleTopLevelFormChange = (ev, field) => {
    const val = ev.target.value;
    this.props.inventories.forEach((i, idx) => {
      if (this.props.inventoriesSelected.includes(i.id)) {
        this.props.change(`inventories[${idx}].${field}`, val);
      }
    });
  };

  renderLineItems = ({ fields, canEditSrpReason }) =>
    fields.map(item => {
      const currentExchangeSrp = this.props.getFieldValue(
        `${item}.exchange_srp`
      );

      const suggestedRetail = this.props.getFieldValue(
        `${item}.${SUGGESTED_RETAIL}`
      );

      const inventory = this.props.inventories.find(
        i => i.id === this.props.getFieldValue(`${item}.id`)
      );

      if (!inventory) return null;
      const selected = this.props.inventoriesSelected.includes(inventory.id);
      return (
        <tr key={inventory.id} className="striped">
          <InventoryStatusCell inventory={inventory}>
            <input
              type="checkbox"
              className="radio-inline"
              style={{ marginLeft: 10 }}
              checked={selected}
              onChange={() => this.toggleInventory(inventory.id, item)}
            />
          </InventoryStatusCell>
          <td style={{ paddingLeft: 10 }}>
            <FormRow>
              <MinimumSrpWarning value={currentExchangeSrp} />
              <AdjustedLast30DaysWarning
                selected={selected}
                inventory={inventory}
              />
              {Object.keys(displayFields)
                .slice(0, 4)
                .map(k => {
                  if (k === SRP_CONDITION) {
                    return (
                      <Col md={3}>
                        <Field
                          name={`${item}.${k}`}
                          component={Select}
                          options={toOptions(INVENTORIES.condition)}
                          label="SRP Condition"
                        />
                      </Col>
                    );
                  }
                  const FieldComponent =
                    k === TAG_DATE ? MonthYearField : Field;
                  return (
                    <Col md={3}>
                      <FieldComponent
                        component={FormInput}
                        name={`${item}.${k}`}
                        label={displayFields[k].label}
                        type={displayFields[k].type}
                        disabled={![SUGGESTED_RETAIL, EXCHANGE_SRP].includes(k)}
                      />
                    </Col>
                  );
                })}
            </FormRow>
            <FormRow>
              {Object.keys(displayFields)
                .slice(4)
                .map(k => {
                  if (k === SRP_CONDITION) {
                    return (
                      <Col md={3}>
                        <Field
                          name={`${item}.${k}`}
                          component={Select}
                          options={toOptions(INVENTORIES.condition)}
                          label="SRP Condition"
                        />
                      </Col>
                    );
                  }

                  if (k === SRP_REASON_CHANGED) {
                    if (!canEditSrpReason) return null;
                    return (
                      <Col md={3}>
                        <Field
                          name={`${item}.${k}`}
                          component={Select}
                          label="Reason for SRP Change"
                          options={toOptions(SRP_CHANGE_REASONS)}
                        />
                      </Col>
                    );
                  }

                  const FieldComponent =
                    k === TAG_DATE ? MonthYearField : Field;
                  return (
                    <Col md={3}>
                      <FieldComponent
                        component={FormInput}
                        name={`${item}.${k}`}
                        label={displayFields[k].label}
                        type={displayFields[k].type}
                        disabled={![SUGGESTED_RETAIL, EXCHANGE_SRP].includes(k)}
                      />
                    </Col>
                  );
                })}
            </FormRow>
            <RepairWarning
              selected={selected}
              suggestedRetail={suggestedRetail}
              inventory={inventory}
            />
          </td>
        </tr>
      );
    });

  render() {
    const { role } = this.props;

    const canEditSrpReason = [ADMIN, QUALITY_CONTROL].includes(role);

    const colLength = canEditSrpReason ? 3 : 4;

    return (
      <AppForm {...this.props}>
        <AppForm.Body>
          <div>
            <FormRow>
              <Col md={colLength}>
                <Field
                  component={FormInput}
                  name="srp"
                  label="Update SRP"
                  type="number"
                  onChange={e =>
                    this.handleTopLevelFormChange(e, SUGGESTED_RETAIL)}
                />
              </Col>
              <Col md={colLength}>
                <Field
                  component={FormInput}
                  name={EXCHANGE_SRP}
                  label="Update Exchange SRP"
                  type="number"
                  onChange={e => this.handleTopLevelFormChange(e, EXCHANGE_SRP)}
                />
              </Col>
              <Col md={colLength}>
                <Field
                  component={Select}
                  name={SRP_CONDITION}
                  options={toOptions(INVENTORIES.condition)}
                  label="Toggle By SRP Condition"
                  onChange={e => this.toggleCondition(e.target.value)}
                />
              </Col>
              {canEditSrpReason && (
                <Col md={colLength}>
                  <Field
                    component={Select}
                    name={SRP_REASON_CHANGED}
                    label="Reason for SRP Change"
                    options={toOptions(SRP_CHANGE_REASONS)}
                    onChange={e =>
                      this.handleTopLevelFormChange(e, SRP_REASON_CHANGED)}
                  />
                </Col>
              )}
            </FormRow>
            <DashedSeparator />
            <table className="table table-striped table-numbered margin-bottom-none">
              <tbody>
                <FieldArray
                  name="inventories"
                  component={this.renderLineItems}
                  canEditSrpReason={canEditSrpReason}
                />
              </tbody>
            </table>
          </div>
        </AppForm.Body>
      </AppForm>
    );
  }
}

Form.propTypes = {
  change: T.func.isRequired,
  close: T.func.isRequired,
  getFieldValue: T.func.isRequired,
  getInitialFieldValue: T.func.isRequired,
  handleSubmit: T.func.isRequired,
  invalid: T.bool.isRequired,
  inventories: T.arrayOf(T.shape),
  inventoriesSelected: T.arrayOf(T.number),
  setInventoriesSelected: T.func.isRequired,
  submitting: T.bool.isRequired
};

const formName = "editSrps";

export default connect(
  state => ({
    getFieldValue: field => formValueSelector(formName)(state, field),
    getInitialFieldValue: field =>
      _.get(getFormInitialValues(formName)(state), field),
    role: state.session.getIn([USER, ROLE]),
    minExchangeSrp: state.session.getIn([SETTINGS, MINIMUM_EXCHANGE_SRP])
  }),
  (dispatch, ownProps) => ({
    onSubmit: data => {
      dispatch(
        bulkUpdate({
          inventories: data.inventories.filter(i =>
            ownProps.inventoriesSelected.includes(i.id)
          )
        })
      )
        .then(() => {
          ownProps.close();
        })
        .catch(error => {
          toastr.error("Error", error.message);
        });
    }
  })
)(
  reduxForm({
    form: formName,
    validate
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/inventories/components/Detail/Header/DropdownActions/UpdateSrpStatus/Form.jsx